import React from 'react';
import { Alert, Box, Button, Chip, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ISaleOrderSummary } from 'features/transaction-details/types';
import { formatTransactionAmount } from 'features/transaction-details/helpers';
import { AuthorizationRefundImpact } from 'features/transaction-details/helpers/transactionRefundImpact';
import { formatDateToShowUser } from 'features/common/helpers';
import { RefundImpactNavigationButtons } from '../RefundImpactNavigationButtons';

interface TransactionAssociatedSaleOrderProps {
	saleOrder: ISaleOrderSummary | null;
	crmTicket: number;
	refundImpacts?: AuthorizationRefundImpact[];
	coinCode?: string;
	onCopy: (text: string, label: string) => void;
	onNavigationError?: (message: string) => void;
}

export const TransactionAssociatedSaleOrder = ({
	saleOrder,
	crmTicket,
	refundImpacts = [],
	coinCode = 'USD',
	onCopy,
	onNavigationError,
}: TransactionAssociatedSaleOrderProps): JSX.Element => {
	const fmt = (amount: number): string => formatTransactionAmount(amount, coinCode);
	const handleOpenSaleOrder = (): void => {
		if (saleOrder) {
			window.open(`/salesorder/${saleOrder.id}`, '_blank');
		}
	};

	// Hay sale orders sin fecha de viaje (el campo directamente no viene en la respuesta):
	// si no hay fecha, no mostramos la línea en vez de dejar un "Fecha de viaje:" vacío.
	const travelDepartureDateLabel = formatDateToShowUser(saleOrder?.travelDepartureDate);

	if (!saleOrder) {
		return (
			<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<ConfirmationNumberIcon color="action" fontSize="small" />
					<Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
						Sale order asociada a la transacción
					</Typography>
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						No se encontró sale order. CRM Ticket: {crmTicket}
					</Typography>
					<Tooltip title="Copiar CRM Ticket">
						<IconButton size="small" onClick={() => onCopy(crmTicket.toString(), 'CRM Ticket')}>
							<ContentCopyIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			</Paper>
		);
	}

	return (
		<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
				<ShoppingCartIcon color="primary" fontSize="small" />
				<Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
					Sale order asociada a la transacción
				</Typography>
			</Box>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
					<Typography
						variant="body2"
						fontWeight="medium"
						sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-all' }}
					>
						{saleOrder.id}
					</Typography>
					<Tooltip title="Copiar Sale Order ID">
						<IconButton size="small" onClick={() => onCopy(saleOrder.id, 'Sale Order ID')}>
							<ContentCopyIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Button
						size="small"
						variant="outlined"
						startIcon={<OpenInNewIcon />}
						onClick={handleOpenSaleOrder}
						sx={{ ml: { sm: 'auto' } }}
					>
						Abrir
					</Button>
				</Box>

				<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
					<Chip label={`Nº ${saleOrder.orderNumber}`} size="small" variant="outlined" />
					<Chip icon={<ConfirmationNumberIcon />} label={`Deal: ${saleOrder.dealId}`} size="small" variant="outlined" />
					<Chip label={saleOrder.mainPassengerCompleteName} size="small" variant="outlined" />
					<Chip label={saleOrder.destinations} size="small" variant="outlined" />
					<Chip
						label={formatTransactionAmount(saleOrder.saleAmount, saleOrder.currency)}
						size="small"
						color="success"
						variant="outlined"
					/>
				</Box>

				{travelDepartureDateLabel && (
					<Typography variant="caption" color="text.secondary">
						Fecha de viaje: {travelDepartureDateLabel}
					</Typography>
				)}

				{refundImpacts.length > 0 && (
					<Alert severity="info" sx={{ mt: 1 }}>
						<Typography variant="body2" component="div">
							Hay devoluciones en esta transacción ({refundImpacts.map((i) => `#${i.authorizationId}`).join(', ')}
							). Los montos devueltos ({refundImpacts.map((i) => fmt(i.totalRefunded)).join(', ')}) vuelven a quedar
							pendientes de autorizar en la transacción. El <strong>caso de venta {crmTicket}</strong> recupera saldo
							pendiente con el cliente hasta que se registre una nueva autorización.
						</Typography>
						<RefundImpactNavigationButtons dealId={crmTicket} saleOrderId={saleOrder?.id} onError={onNavigationError} />
					</Alert>
				)}
			</Box>
		</Paper>
	);
};
